import api, { getErrorMessage } from './api';

const recognitionsService = {
  /**
   * Get current user's recognitions (sent and received)
   * @returns {Promise<object>} - Recognitions list
   */
  getMyRecognitions: async () => {
    try {
      const response = await api.get('/api/v1/recognitions/me');
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  },

  /**
   * Create a new recognition
   * @param {object} recognitionData - Recognition data
   * @param {string} recognitionData.recipient_id - Recipient user ID
   * @param {string} recognitionData.message - Recognition message
   * @param {number} recognitionData.points_amount - Optional points to transfer
   * @returns {Promise<object>} - Created recognition
   */
  createRecognition: async (recognitionData) => {
    try {
      const response = await api.post('/api/v1/recognitions/', recognitionData);
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  },

  /**
   * Update visibility on an existing recognition.
   * Allowed for sender, org admins (in same org), or superusers.
   * @param {string} recognitionId - Recognition ID
   * @param {object} body - { visibility, visible_team_id }
   * @returns {Promise<object>} - Updated recognition
   */
  updateVisibility: async (recognitionId, body) => {
    try {
      const response = await api.patch(
        `/api/v1/recognitions/${recognitionId}/visibility`,
        body
      );
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  },

  updateMessage: async (recognitionId, message) => {
    try {
      const response = await api.patch(
        `/api/v1/recognitions/${recognitionId}/message`,
        { message }
      );
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  },

  /**
   * Delete a recognition (and all batch siblings shown on the same card).
   * Allowed for the sender, org admins, and team admins of the recognition's
   * visibility/budget team.
   * @param {string} recognitionId - Recognition ID
   * @param {object} [options]
   * @param {boolean} [options.reversePoints=false] - Also claw back credited
   *   points and refund the team budget.
   * @returns {Promise<object>} - { deleted_ids, points_reversed, reversed_points_total }
   */
  deleteRecognition: async (recognitionId, { reversePoints = false } = {}) => {
    try {
      const response = await api.delete(
        `/api/v1/recognitions/${recognitionId}`,
        { params: { reverse_points: reversePoints } }
      );
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  },

  /**
   * Get all recognitions (admin only)
   * @param {number} skip - Number of records to skip
   * @param {number} limit - Number of records to return
   * @returns {Promise<object>} - Paginated recognitions list
   */
  getAllRecognitions: async (skip = 0, limit = 100) => {
    try {
      const response = await api.get('/api/v1/admin/recognitions/', {
        params: { skip, limit },
      });
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  },

  /**
   * Get recognition by ID (admin only)
   * @param {string} recognitionId - Recognition ID
   * @returns {Promise<object>} - Recognition data
   */
  getRecognitionById: async (recognitionId) => {
    try {
      const response = await api.get(`/api/v1/admin/recognitions/${recognitionId}`);
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  },

  /**
   * Get all recognitions (admin only)
   * Note: Backend currently only supports skip and limit parameters
   * @param {object} params - Query parameters
   * @param {number} params.skip - Number of records to skip
   * @param {number} params.limit - Number of records to return
   * @returns {Promise<Array>} - Recognitions list
   */
  listAll: async (params = {}) => {
    try {
      // Backend only supports skip and limit, ignore other filters for now
      const { skip, limit } = params;
      const cleanParams = {};
      if (skip !== undefined && skip !== null && skip !== "") cleanParams.skip = skip;
      if (limit !== undefined && limit !== null && limit !== "") cleanParams.limit = limit;

      const response = await api.get('/api/v1/admin/recognitions/', {
        params: cleanParams,
      });
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  },
};

export default recognitionsService;
